/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import Datatable from '@/components/common/Datatable';
import {
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import cartIcon from '../../../assets/svg/shopping-bag.svg?react';
import wishListIcon from '../../../assets/svg/wishlistIcon.svg?react';
import CustomizedIcon from '../../../assets/svg/customizable-icon.svg?react';
import { FiMoreVertical } from 'react-icons/fi';
import eyeIcon from '../../../assets/svg/eye.svg?react';
import editIcon from '../../../assets/svg/editIcon.svg?react';
import deleteIcon from '../../../assets/svg/delete.svg?react';
import ConfirmDeleteIcon from '@/assets/svg/delete-bg.svg?react';
import { useDeleteProduct, useGetProducts } from '@/services/manageProduct';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import { AxiosError } from 'axios';
import useCustomToast from '@/components/common/Toast';
import useErrorHandler from '@/utils/errorHandler';
import { convertDateFormat } from '@/utils/convertDateFormat';
import { useNavigate } from 'react-router-dom';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import { Column, Row } from 'react-table';
import Button from '@/components/common/Button';
import { IProduct } from '@/services/manageProduct/types';

const RejectedProducts = () => {
  const [selectedId, setSelectedId] = useState<number | null>(0);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [search, setQuery] = useState('');
  const [filters, setFilters] = useState<{
    start_date?: string;
    end_date?: string;
    category?: string;
    subcategory?: string;
  }>({
    start_date: '',
    end_date: '',
    category: '',
    subcategory: '',
  });
  const { showToast } = useCustomToast();
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();

  const {
    data: Products,
    refetch,
    isLoading,
    isError,
  } = useGetProducts({
    status: 'rejected',
    page: pagination.page,
    search,
    query: search,
    ...filters,
  });
  const { mutateAsync: deleteProduct, isLoading: isDeleting } = useDeleteProduct();

  useEffect(() => {
    if (Products) {
      setPagination((prev) => ({
        ...prev,
        pageSize: Products.pageSize,
        totalCount: Products.count,
        totalPages: Products.page_count,
      }));
    }
  }, [Products]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const resetFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      category: '',
      subcategory: '',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const openModal = (id: number) => {
    setSelectedId(id);
    onOpen();
  };

  const handleDeleteConfirm = () => {
    if (selectedId !== null) {
      try {
        deleteProduct(selectedId).then(() => {
          setSelectedId(null);
          onClose();
          showToast('success', 'Product deleted Successfully');
        });
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          handleError(error);
        }
      }
    } else return;
  };

  useEffect(() => {
    if (
      filters.start_date === '' &&
      filters.end_date === '' &&
      filters.category === '' &&
      filters.subcategory === ''
    ) {
      refetch();
    }
  }, [filters, pagination.page]);

  const columns: Column<IProduct>[] = useMemo(
    () => [
      {
        Header: 'Product ID',
        accessor: 'id',
      },
      {
        Header: 'Product Name',
        accessor: 'name',
        Cell: ({ row }: { row: Row<IProduct> }) => (
          <HStack gap={2} alignItems={'end'} minWidth={'300px'}>
            <Image
              h={'48px'}
              w={'48px'}
              minW="48px"
              maxW="48px"
              maxH="48px"
              borderRadius={'lg'}
              src={
                row.original.primary_image ? row.original.primary_image.image_small : '' // or use a placeholder SVG/component
              }
              alt={row.original.name}
              objectFit={'cover'}
              objectPosition={'top'}
            />
            <VStack gap={1} align="start" justifyContent="end" h="full">
              <Text>{row.original.name}</Text>
              <HStack gap={1}>
                {row.original.is_customizable && (
                  <HStack bg={'#F2F4F8'} borderRadius={'3px'} p={1}>
                    <Box p="2px" display="flex" alignItems="end">
                      <Icon as={CustomizedIcon} color={'brand.primary.DEFAULT'} />
                    </Box>
                  </HStack>
                )}
                <HStack bg={'#F2F4F8'} borderRadius={'3px'} p={1}>
                  <Box p="2px" display="flex" alignItems="end">
                    <Icon as={cartIcon} color={'brand.primary.DEFAULT'} />
                    <Text ml="4px" lineHeight={0.8} color={'text.secondary'}>
                      {row.original.cart_item_count ?? 0}
                    </Text>
                  </Box>
                  <Box p="2px" display="flex" alignItems="end">
                    <Icon as={wishListIcon} color={'brand.primary.DEFAULT'} />
                    <Text ml="4px" lineHeight={0.8} color={'text.secondary'}>
                      {row.original.wishlist_count ?? 0}
                    </Text>
                  </Box>
                </HStack>
              </HStack>
            </VStack>
          </HStack>
        ),
      },
      {
        Header: 'Category',
        accessor: 'category',
        Cell: ({ row }: { row: Row<IProduct> }) => {
          const category = row.original.category;
          return category ? (
            <Text minWidth={'100px'}>{category}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Sub-category',
        accessor: 'sub_category',
        Cell: ({ row }: { row: Row<IProduct> }) => {
          const subCategory = row.original.sub_category;
          return subCategory ? (
            <Text minWidth={'200px'}>{subCategory}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Reason of Rejection',
        accessor: 'comment',
        Cell: ({ row }: { row: Row<IProduct> }) => {
          const comment = row.original.comment;
          return comment ? (
            <Text maxWidth={'400px'} minWidth={'200px'}>
              {comment}
            </Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Submission Date',
        accessor: 'created_at',
        Cell: ({ row }: { row: Row<IProduct> }) => {
          const createdAt = row.original.created_at;
          return <Text minWidth={'100px'}>{convertDateFormat(createdAt)}</Text>;
        },
      },
      {
        id: 'actions',
        Header: 'Actions',
        Cell: ({ row }: { row: Row<IProduct> }) => (
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<FiMoreVertical color="brand.primary.DEFAULT" />}
              variant=""
            />
            <MenuList>
              <MenuItem
                onClick={() =>
                  navigate(
                    NAVIGATION_ROUTES.VIEW_PRODUCT.replace(
                      ':viewProductId',
                      String(row.original.id),
                    ),
                  )
                }
              >
                <Flex gap={2} align="center">
                  <Icon as={eyeIcon} boxSize={4} color={'text.primary'} />
                  <Text color={'text.primary'} fontSize={'sm'} mt={1}>
                    View Detail
                  </Text>
                </Flex>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  navigate(
                    NAVIGATION_ROUTES.EDIT_PRODUCT.replace(
                      ':editProductId',
                      String(row.original.id),
                    ),
                    {
                      state: { fromTab: 3 },
                    },
                  )
                }
              >
                <Flex gap={2} align="center">
                  <Icon as={editIcon} boxSize={4} color={'text.primary'} />
                  <Text color={'text.primary'} fontSize={'sm'} mt={1}>
                    Edit
                  </Text>
                </Flex>
              </MenuItem>
              <MenuItem onClick={() => openModal(row.original.id)}>
                <Flex gap={2} align="center">
                  <Icon as={deleteIcon} boxSize={4} color={'status.error'} />
                  <Text color={'status.error'} mt={1}>
                    Delete
                  </Text>
                </Flex>
              </MenuItem>
            </MenuList>
          </Menu>
        ),
      },
    ],
    [Products],
  );

  return (
    <Box bg={'background.white'}>
      <Datatable
        data={Products?.results ?? []}
        isLoading={isLoading}
        isError={isError}
        columns={columns}
        headerButton={
          <Button onClick={() => navigate(NAVIGATION_ROUTES.ADD_PRODUCT)}>+ Add Product</Button>
        }
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        filters={filters}
        setFilters={setFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <CustomConfirmationModal
        isLoading={isDeleting}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDeleteConfirm}
        icon={ConfirmDeleteIcon}
        title="Delete Product?"
        subtitle="Are you sure you want to delete this product?"
        description="This action cannot be undone, and you won't be able to track this product anywhere. This will remove the product permanently."
        buttonText="Confirm"
      />
    </Box>
  );
};

export default RejectedProducts;
